import api from '@/libs/axiosInstance';
import { shouldRetryDashboardQuery } from '@/libs/query-client';
import { useAuth } from '@/contexts/auth-context';
import { PlanTier } from '@fluenceur/types';
import type {
  ApiResponse,
  OrganizationFeaturesResponse,
  FeatureFlag,
  FeatureLimits,
} from '@fluenceur/types';
import { useQuery } from '@tanstack/react-query';

/**
 * Fetch organization features from the API
 */
const fetchFeatures = async (): Promise<OrganizationFeaturesResponse> => {
  const response = await api.get<ApiResponse<OrganizationFeaturesResponse>>('/dashboard/features');

  if (!response.data.success) {
    throw new Error(response.data.message || 'Failed to fetch features');
  }

  if (!response.data.data) {
    throw new Error('No features data returned');
  }

  return response.data.data;
};

/**
 * Query key for plan features
 */
export const planFeaturesQueryKey = ['planFeatures'] as const;

/**
 * Hook to fetch organization features and plan details
 *
 * Features:
 * - Automatic caching with TanStack Query
 * - 5 minute stale time (features rarely change)
 * - Only fetches when authenticated
 * - Provides helper methods for feature/limit checks
 *
 * @example
 * ```tsx
 * const { hasFeature, canCreateBrand, isLoading } = usePlanFeaturesQuery();
 *
 * if (hasFeature(FeatureFlag.MULTI_BRAND)) {
 *   // Show multi-brand UI
 * }
 *
 * if (canCreateBrand) {
 *   // Show create brand button
 * }
 * ```
 */
export function usePlanFeaturesQuery() {
  const { isAuthenticated } = useAuth();

  const query = useQuery({
    queryKey: planFeaturesQueryKey,
    queryFn: fetchFeatures,
    staleTime: 5 * 60 * 1000, // 5 minutes - features rarely change
    enabled: isAuthenticated,
    retry: (failureCount, error) => shouldRetryDashboardQuery(failureCount, error, 1),
  });

  const { data: features } = query;

  /**
   * Check if a specific feature is enabled
   */
  const hasFeature = (feature: FeatureFlag): boolean => {
    if (!features) return false;
    const resolvedFeature = features.features.find((f) => f.feature === feature);
    return resolvedFeature?.enabled ?? false;
  };

  /**
   * Get the current limit value for a specific limit type
   */
  const getLimit = (limitName: keyof FeatureLimits): number => {
    if (!features) return 1;
    const limit = features.limits.find((l) => l.name === limitName);
    return limit?.value ?? 1;
  };

  /**
   * Check if a limit has been exceeded
   */
  const isLimitExceeded = (limitName: keyof FeatureLimits): boolean => {
    if (!features) return false;
    const limit = features.limits.find((l) => l.name === limitName);
    return limit?.exceeded ?? false;
  };

  /**
   * Get current usage for a limit
   */
  const getLimitUsage = (limitName: keyof FeatureLimits): number => {
    if (!features) return 0;
    const limit = features.limits.find((l) => l.name === limitName);
    return limit?.currentUsage ?? 0;
  };

  /**
   * Check if user can create a new brand (based on multi-brand feature and limits)
   */
  const canCreateBrand = (): boolean => {
    if (!features) return false;
    const maxBrandsLimit = features.limits.find((l) => l.name === 'maxBrands');
    if (!maxBrandsLimit) return false;
    return !maxBrandsLimit.exceeded;
  };

  return {
    ...query,
    // Helper properties
    planTier: features?.planTier ?? PlanTier.FREE_BETA,
    hasActiveOverrides: features?.hasActiveOverrides ?? false,
    // Helper methods
    hasFeature,
    getLimit,
    isLimitExceeded,
    getLimitUsage,
    canCreateBrand,
  };
}
